import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { getJobDetails, adminUpdateJob, createInvoiceAndRcti, getJobAuditLog } from '../services/jobService';
import { getInvoiceDetails, voidInvoice } from '../services/invoiceService';
import { getRCTIDetails, voidRCTI } from '../services/rctiService';
import { searchFranchisees } from '../services/franchiseeService';
import { listServiceTypes } from '../services/serviceTypeService';
import { debounce } from 'lodash';
import { FaMapMarkerAlt, FaCheck, FaExclamationTriangle, FaCalendarCheck, FaInfoCircle, FaPencilAlt, FaMedkit, FaCalendarAlt, FaFileInvoice, FaHistory, FaChevronDown, FaChevronUp, FaTrashAlt, FaSync } from 'react-icons/fa';
import { terminology, allocationTypes, formatters } from '../config/terminology';
import { useAppConfig } from '../context/AppConfigContext';
import CompletionProgressModal from '../components/CompletionProgressModal';
import { InputField, SelectField, TextareaField, ReadOnlyField } from '../components/fields';
import { Button } from '../components/forms';
import { isDemoMode } from '../utils/demoMode';
import ConfirmationModal from '../components/ConfirmationModal';

const formatTimestampForInput = (timestamp) => {
    if (!timestamp || !timestamp._seconds) return '';
    const date = new Date(timestamp._seconds * 1000);
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - timezoneOffset);
    return localDate.toISOString().split('T')[0];
};

const formatTimestampForDisplay = (timestamp, includeTime = false) => {
    if (!timestamp || !timestamp._seconds) return 'N/A';
    const date = new Date(timestamp._seconds * 1000);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = true;
    }
    return date.toLocaleDateString('en-AU', options);
};

const parseInvoiceGenerationError = (rawError) => {
    if (!rawError || typeof rawError !== 'string') {
        return {
            failedTypes: [],
            technicalMessages: []
        };
    }

    const technicalMessages = rawError
        .split(';')
        .map(part => part.trim())
        .filter(Boolean);

    const failedTypes = [];

    technicalMessages.forEach((message) => {
        const normalized = message.toLowerCase();

        if (normalized.startsWith('invoice:')) {
            failedTypes.push('invoice');
        } else if (normalized.startsWith('rcti:')) {
            failedTypes.push('rcti');
        }
    });

    return {
        failedTypes: [...new Set(failedTypes)],
        technicalMessages
    };
};

const useSearch = (searchFunction, options = {}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const optionsString = JSON.stringify(options);

    const debouncedFetch = useCallback(debounce(async (term, fetchOptions) => {
        if (!searchFunction || term.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        setIsSearching(true);
        try {
            const data = await searchFunction({ searchString: term, ...fetchOptions });
            const results = data.franchisees || data.clients || data.households || [];
            setSuggestions(results);
            setShowSuggestions(true);
        } catch (error) {
            console.error("Search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsSearching(false);
        }
    }, 300), [searchFunction]);

    useEffect(() => {
        const currentOptions = JSON.parse(optionsString);
        debouncedFetch(searchTerm, currentOptions);
        return () => debouncedFetch.cancel();
    }, [searchTerm, optionsString, debouncedFetch]);

    return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};

const today = new Date();
today.setHours(0, 0, 0, 0);
const todayTimestampSeconds = Math.floor(today.getTime() / 1000);

const EditJobPage = () => {
    const { id: jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const filtersFromNav = location.state?.filters || {}; // Get filters from navigation state
    const returnSearch = location.state?.returnSearch || null;
    const [apiError, setApiError] = useState(null);
    const [saveSuccessMessage, setSaveSuccessMessage] = useState(null);
    const [completionWarnings, setCompletionWarnings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [originalJobData, setOriginalJobData] = useState(null);

    const [allServiceTypes, setAllServiceTypes] = useState([]);
    const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [serviceSelectionError, setServiceSelectionError] = useState(null);
    const [statusChangeValidationError, setStatusChangeValidationError] = useState(null);
    const errorRef = useRef(null);
    const [showInvoiceRctiModal, setShowInvoiceRctiModal] = useState(false);
    const [invoiceRctiCreating, setInvoiceRctiCreating] = useState(false);
    const [invoiceRctiResult, setInvoiceRctiResult] = useState(null);
    const [showCompletionProgress, setShowCompletionProgress] = useState(false);
    const [completionStep, setCompletionStep] = useState('saving');
    const [completionStepResults, setCompletionStepResults] = useState({});
    const [invoiceVoided, setInvoiceVoided] = useState(false);
    const [rctiVoided, setRctiVoided] = useState(false);

    // Void invoice/RCTI state
    const [invoiceStatus, setInvoiceStatus] = useState(null);
    const [rctiStatus, setRctiStatus] = useState(null);
    const [voidingInvoice, setVoidingInvoice] = useState(false);
    const [voidingRCTI, setVoidingRCTI] = useState(false);
    const [showVoidInvoiceConfirm, setShowVoidInvoiceConfirm] = useState(false);
    const [showVoidRCTIConfirm, setShowVoidRCTIConfirm] = useState(false);

    const [auditLog, setAuditLog] = useState([]);
    const [auditLogLoading, setAuditLogLoading] = useState(false);
    const [auditLogExpanded, setAuditLogExpanded] = useState(false);

    const { loading: configLoading, error: configError, getStatuses } = useAppConfig();
    const jobStatuses = useMemo(() => getStatuses() || [], [getStatuses]);

    const { register, handleSubmit, watch, setValue, reset, formState: { errors, isSubmitting, isDirty } } = useForm({
        mode: 'onBlur',
        defaultValues: {
            fseId: '',
            fseNameSearch: '',
            clientName: '',
            householdName: '',
            householdAddress: '',
            jobDate: '',
            jobTime: '',
            allocationType: '',
            allotedHours: '',
            actualHours: '',
            pricePerHour: '',
            additionalPrice: '',
            priceQuotePerHour: '',
            invoicePricePerHour: '',
            status: '',
            completionDate: '',
            description: '',
            adminNotes: '',
            notesForFranchisee: '',
            servicesDirty: false,
        }
    });

    const allocationType = watch('allocationType');
    const pricePerHour = watch('pricePerHour');
    const currentStatus = watch('status');
    const additionalPrice = watch('additionalPrice');
    const isDateTimeLocked = currentStatus === 'completed' || currentStatus === 'invoiced';

    const isOverdue = useMemo(() => {
        if (!originalJobData) return false;
        return originalJobData.status === 'approved' &&
               originalJobData.jobDate?._seconds &&
               originalJobData.jobDate._seconds < todayTimestampSeconds;
    }, [originalJobData]);

    const isServiceDelayed = useMemo(() => {
        return originalJobData?.status === 'service-delayed';
    }, [originalJobData]);

    const invoiceGenerationFailureInfo = useMemo(() => {
        if (originalJobData?.invoiceGenerationStatus !== 'failed') {
            return null;
        }

        const { failedTypes, technicalMessages } = parseInvoiceGenerationError(originalJobData.invoiceGenerationError);

        let userFriendlyMessage = 'Invoice/RCTI generation failed. Please try again using the button above, or contact support.';

        if (failedTypes.length === 2) {
            userFriendlyMessage = 'Invoice and RCTI generation failed. Please try again using the button above, or contact support.';
        } else if (failedTypes[0] === 'invoice') {
            userFriendlyMessage = 'Invoice generation failed. Please try again using the button above, or contact support.';
        } else if (failedTypes[0] === 'rcti') {
            userFriendlyMessage = 'RCTI generation failed. Please try again using the button above, or contact support.';
        }

        return {
            userFriendlyMessage,
            technicalMessages
        };
    }, [originalJobData]);

    const invoiceRctiActionLabel = useMemo(() => {
        if (originalJobData?.invoiceId && !originalJobData?.rctiId) {
            return 'Create RCTI Only';
        }

        if (!originalJobData?.invoiceId && originalJobData?.rctiId) {
            return 'Create Invoice Only';
        }

        return 'Create Invoice & RCTI';
    }, [originalJobData]);

    const fetchJob = useCallback(async () => {
        setLoading(true);
        setApiError(null);
        setSaveSuccessMessage(null);
        try {
            const jobData = await getJobDetails(jobId);
            setOriginalJobData(jobData);

            const contactName = `${jobData.franchiseeInfo?.firstName || ''} ${jobData.franchiseeInfo?.lastName || ''}`.trim();
            const fseDisplayName = `${jobData.franchiseeInfo?.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            const householdDisplayName = `${jobData.householdInfo?.firstName || ''} ${jobData.householdInfo?.lastName || ''}`.trim() || 'N/A';
            const householdAddress = `${jobData.householdInfo?.addressLine1 || ''}${jobData.householdInfo?.addressLine2 ? ', ' + jobData.householdInfo.addressLine2 : ''}, ${jobData.householdInfo?.state || ''} ${jobData.householdInfo?.postalCode || ''}`.replace(/^, /,'').replace(/, $/,'').trim() || 'No address';

            const initialServiceIds = Array.isArray(jobData.services)
                ? jobData.services.map(s => s.serviceId || s.serviceTypeId).filter(Boolean)
                : [];
            setSelectedServiceIds(initialServiceIds);

            // Jobs inherit allocation type from schedule - just map backend values to UI values
            let allocationTypeUI = 'fixed-fee'; // Default UI allocation type
            
            if (jobData.allocationType === 'set-fee' || jobData.allocationType === 'fixed-fee') {
                allocationTypeUI = 'fixed-fee';
            } else if (jobData.allocationType === 'monthly' || jobData.allocationType === 'days' || jobData.allocationType === 'weeks' || jobData.allocationType === 'fixed-hours') {
                allocationTypeUI = 'fixed-hours';
            }

            reset({
                fseId: jobData.fseId || '',
                fseNameSearch: fseDisplayName,
                clientName: jobData.clientInfo?.clientName || 'N/A',
                householdName: householdDisplayName,
                householdAddress: householdAddress,
                jobDate: formatTimestampForInput(jobData.jobDate),
                jobTime: jobData.jobTime || '',
                allocationType: allocationTypeUI,
                allotedHours: jobData.allotedHours !== null ? String(jobData.allotedHours) : '',
                actualHours: jobData.actualHours !== null ? String(jobData.actualHours) : '',
                pricePerHour: jobData.pricePerHour !== null ? String(jobData.pricePerHour) : '',
                additionalPrice: jobData.additionalPrice !== null ? String(jobData.additionalPrice) : '',
                priceQuotePerHour: jobData.priceQuotePerHour !== null ? String(jobData.priceQuotePerHour) : '',
                invoicePricePerHour: jobData.invoicePricePerHour !== null ? String(jobData.invoicePricePerHour) : '',
                status: jobData.status || '',
                completionDate: formatTimestampForInput(jobData.completionDate),
                description: jobData.description || '',
                adminNotes: jobData.adminNotes || '',
                notesForFranchisee: jobData.notesForFranchisee || '',
                servicesDirty: false,
            }, { keepValues: false });

        } catch (err) {
            console.error("Failed to fetch job details:", err);
            setApiError(err.message || 'Failed to load job details.');
            setOriginalJobData(null);
        } finally {
            setLoading(false);
        }
    }, [jobId, reset]);

    useEffect(() => {
        fetchJob();
    }, [fetchJob]);

    useEffect(() => {
        const fetchServices = async () => {
            setServiceTypesLoading(true);
            try {
                const services = await listServiceTypes();
                setAllServiceTypes(services);
            } catch (error) {
                console.error("Failed to fetch service types:", error);
                setApiError("Could not load service types. Please try again later.");
            } finally {
                setServiceTypesLoading(false);
            }
        };
        fetchServices();
    }, []);

    useEffect(() => {
        const additionalPriceString = additionalPrice;
        const isNdisJob = originalJobData?.ndisJob || false;

        if (additionalPriceString !== null && additionalPriceString !== undefined && additionalPriceString !== '' && !isNaN(parseFloat(additionalPriceString))) {
            const invoicePriceExGST = parseFloat(additionalPriceString);

            if (invoicePriceExGST >= 0) {
                // For NDIS jobs, don't add GST; for non-NDIS jobs, add 10% GST
                const invoicePriceWithGST = isNdisJob 
                    ? invoicePriceExGST.toFixed(2) 
                    : (invoicePriceExGST * 1.1).toFixed(2);
                setValue('invoicePricePerHour', invoicePriceWithGST, { shouldValidate: true, shouldDirty: true });
            } else {
                setValue('invoicePricePerHour', '', { shouldValidate: true, shouldDirty: true });
            }
        } else if (additionalPriceString === '' || additionalPriceString === null || additionalPriceString === undefined) {
             setValue('invoicePricePerHour', '', { shouldValidate: true, shouldDirty: true });
        }
    }, [additionalPrice, setValue, originalJobData?.ndisJob]);

    useEffect(() => {
        let timer;
        if (saveSuccessMessage) {
          timer = setTimeout(() => {
            setSaveSuccessMessage(null);
          }, 5000);
        }
        return () => clearTimeout(timer);
      }, [saveSuccessMessage]);

    useEffect(() => {
        if ((statusChangeValidationError || apiError) && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [statusChangeValidationError, apiError]);

    const fetchAuditLog = useCallback(async () => {
        setAuditLogLoading(true);
        try {
            const entries = await getJobAuditLog(jobId);
            setAuditLog(entries);
        } catch (err) {
            console.error('Failed to fetch audit log:', err);
            setAuditLog([]);
        } finally {
            setAuditLogLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        fetchAuditLog();
    }, [fetchAuditLog]);

    useEffect(() => {
        const fetchVoidStatus = async () => {
            const invoiceId = originalJobData?.invoiceId;
            const rctiId = originalJobData?.rctiId;

            if (invoiceId) {
                try {
                    const invoiceData = await getInvoiceDetails(invoiceId);
                    setInvoiceVoided(invoiceData.status === 'void' || invoiceData.xero_invoice_status === 'VOIDED');
                } catch (err) {
                    console.error('[EditJobPage] Failed to fetch invoice void status:', err);
                    setInvoiceVoided(false);
                }
            } else {
                setInvoiceVoided(false);
            }

            if (rctiId) {
                try {
                    const rctiData = await getRCTIDetails(rctiId);
                    setRctiVoided(rctiData.status === 'void' || rctiData.xero_invoice_status === 'VOIDED');
                } catch (err) {
                    console.error('[EditJobPage] Failed to fetch RCTI void status:', err);
                    setRctiVoided(false);
                }
            } else {
                setRctiVoided(false);
            }
        };

        fetchVoidStatus();
    }, [originalJobData?.invoiceId, originalJobData?.rctiId]);

    // Fetch invoice/RCTI status when job data loads
    useEffect(() => {
        const fetchInvoiceRctiStatus = async () => {
            if (originalJobData?.invoiceId) {
                try {
                    const invoiceData = await getInvoiceDetails(originalJobData.invoiceId);
                    setInvoiceStatus(invoiceData?.status || null);
                } catch (err) {
                    console.error('Failed to fetch invoice status:', err);
                }
            }
            if (originalJobData?.rctiId) {
                try {
                    const rctiData = await getRCTIDetails(originalJobData.rctiId);
                    setRctiStatus(rctiData?.status || null);
                } catch (err) {
                    console.error('Failed to fetch RCTI status:', err);
                }
            }
        };
        fetchInvoiceRctiStatus();
    }, [originalJobData?.invoiceId, originalJobData?.rctiId]);

    const handleVoidInvoice = async () => {
        setShowVoidInvoiceConfirm(false);
        setVoidingInvoice(true);
        try {
            await voidInvoice(originalJobData.invoiceId);
            setInvoiceStatus('void');
            setSaveSuccessMessage('Invoice voided successfully.');
            fetchJob(); // Refresh job data
        } catch (err) {
            console.error('Error voiding invoice:', err);
            setApiError(err.message || 'Failed to void invoice.');
        } finally {
            setVoidingInvoice(false);
        }
    };

    const handleVoidRCTI = async () => {
        setShowVoidRCTIConfirm(false);
        setVoidingRCTI(true);
        try {
            await voidRCTI(originalJobData.rctiId);
            setRctiStatus('void');
            setSaveSuccessMessage('RCTI voided successfully.');
            fetchJob(); // Refresh job data
        } catch (err) {
            console.error('Error voiding RCTI:', err);
            setApiError(err.message || 'Failed to void RCTI.');
        } finally {
            setVoidingRCTI(false);
        }
    };

    const isInvoiceVoided = invoiceStatus === 'void';
    const isRctiVoided = rctiStatus === 'void';
    const canVoidInvoice = originalJobData?.invoiceId && invoiceStatus && invoiceStatus !== 'void' && invoiceStatus !== 'paid';
    const canVoidRCTI = originalJobData?.rctiId && rctiStatus && rctiStatus !== 'void' && rctiStatus !== 'paid';

    const franchiseeSearch = useSearch(searchFranchisees);

    const fseHandlers = {
        onChange: (e) => {
            const value = e.target.value;
            franchiseeSearch.setSearchTerm(value);
            setValue('fseNameSearch', value, { shouldDirty: true });
            setValue('fseId', '', { shouldDirty: true });
            if (value.length < 3) franchiseeSearch.setShowSuggestions(false);
        },
        onSelect: (item) => {
            setValue('fseId', item.id, { shouldDirty: true });
            const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
            const displayName = `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            setValue('fseNameSearch', displayName, { shouldDirty: true });
            franchiseeSearch.setSuggestions([]);
            franchiseeSearch.setShowSuggestions(false);
        },
        renderSuggestions: (item) => {
            const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
            return `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
        }
    };

    const handleServiceToggle = (serviceId) => {
        setSelectedServiceIds(prev =>
            prev.includes(serviceId)
                ? prev.filter(id => id !== serviceId)
                : [...prev, serviceId]
        );
        setServiceSelectionError(null);
        setValue('servicesDirty', true, { shouldDirty: true });
    };

    const onSubmit = async (data) => {
        setApiError(null);
        setServiceSelectionError(null);
        setStatusChangeValidationError(null);
        setSaveSuccessMessage(null);

        if (selectedServiceIds.length === 0) {
            setServiceSelectionError("Please select at least one service.");
            return;
        }

        const originalStatus = originalJobData?.status;
        const newStatus = data.status;
        const fsePrice = data.pricePerHour;

        if (originalStatus === 'pending-approval' && newStatus === 'approved') {
            if (fsePrice === null || fsePrice === undefined || fsePrice === '' || isNaN(parseFloat(fsePrice)) || parseFloat(fsePrice) < 0) {
                setStatusChangeValidationError('FSE price/hour is required when approving a job.');
                return;
            }
        }

        if (newStatus === 'completed' && (!data.completionDate || data.completionDate === '')) {
            setStatusChangeValidationError('Completion date is required when setting status to completed.');
            return;
        }

        if (newStatus === 'completed' && originalJobData?.allocationType !== 'fixed-fee') {
            const actualHoursVal = parseFloat(data.actualHours);
            if (isNaN(actualHoursVal) || actualHoursVal <= 0) {
                setStatusChangeValidationError('Actual Hours are required and must be greater than 0 when completing a job.');
                return;
            }
        }

        const serviceNamesMap = new Map(allServiceTypes.map(service => [service.id, service.name]));
        const servicesArray = selectedServiceIds.map(id => ({ serviceTypeId: id, name: serviceNamesMap.get(id) })).filter(s => s.name);

        if (servicesArray.length !== selectedServiceIds.length) {
             setServiceSelectionError("Could not find names for all selected services. Please try again.");
             console.error("Failed to map service IDs to names:", selectedServiceIds, serviceNamesMap);
             return;
        }

        const updatePayload = {};
        const originalServices = (originalJobData?.services || []).map(s => s.serviceId || s.serviceTypeId).filter(Boolean).sort();
        const currentServices = [...selectedServiceIds].sort();

        // Transform allocation type: Jobs don't change allocation type - they inherit from schedule
        // But we need to maintain the backend value format
        let finalAllocationType = originalJobData?.allocationType; // Keep original backend value
        
        // Only allow UI changes for display, but don't transform for jobs
        if (data.allocationType !== 'fixed-fee' && data.allocationType !== 'fixed-hours') {
            // If somehow an invalid UI value gets through, keep original
            finalAllocationType = originalJobData?.allocationType || 'fixed-fee';
        }

        if (data.fseId !== originalJobData?.fseId) updatePayload.fseId = data.fseId;
        if (data.jobDate !== formatTimestampForInput(originalJobData?.jobDate)) updatePayload.jobDate = data.jobDate;
        if (data.jobTime !== (originalJobData?.jobTime || '')) updatePayload.jobTime = data.jobTime || null;
        // Note: We don't update allocationType for individual jobs - they inherit from schedule
        if (data.status !== originalJobData?.status) updatePayload.status = data.status;
        if (data.completionDate !== formatTimestampForInput(originalJobData?.completionDate)) updatePayload.completionDate = data.completionDate || null;
        if (data.description !== (originalJobData?.description || '')) updatePayload.description = data.description || null;
        if (data.adminNotes !== (originalJobData?.adminNotes || '')) updatePayload.adminNotes = data.adminNotes || null;
        if (data.notesForFranchisee !== (originalJobData?.notesForFranchisee || '')) updatePayload.notesForFranchisee = data.notesForFranchisee || null;

        const allottedHoursNum = data.allotedHours ? parseFloat(data.allotedHours) : null;
        if (allottedHoursNum !== originalJobData?.allotedHours) updatePayload.allotedHours = allottedHoursNum;

        const actualHoursNum = data.actualHours ? parseFloat(data.actualHours) : null;
        if (actualHoursNum !== originalJobData?.actualHours) updatePayload.actualHours = actualHoursNum;

        const pricePerHourNum = data.pricePerHour ? parseFloat(data.pricePerHour) : null;
        if (pricePerHourNum !== originalJobData?.pricePerHour || (originalStatus === 'pending-approval' && newStatus === 'approved')) {
             updatePayload.pricePerHour = pricePerHourNum;
        }

        const additionalPriceNum = data.additionalPrice ? parseFloat(data.additionalPrice) : null;
        if (additionalPriceNum !== originalJobData?.additionalPrice) updatePayload.additionalPrice = additionalPriceNum;

        const invoicePricePerHourNum = data.invoicePricePerHour ? parseFloat(data.invoicePricePerHour) : null;
        if (invoicePricePerHourNum !== originalJobData?.invoicePricePerHour) updatePayload.invoicePricePerHour = invoicePricePerHourNum;

        if (JSON.stringify(currentServices) !== JSON.stringify(originalServices)) {
            updatePayload.services = servicesArray;
        }

        if (Object.keys(updatePayload).length === 0) {
            setApiError("No changes detected.");
            return;
        }

        console.log("Submitting update payload:", updatePayload);
        console.log("adminUpdateJob parameters - jobId:", jobId, "updatePayload:", updatePayload);

        // Detect if this is a completion action (status changing to 'completed')
        const isCompletionAction = updatePayload.status === 'completed' && originalJobData?.status !== 'completed';

        if (isCompletionAction) {
            // Show stepper modal for completion
            setShowCompletionProgress(true);
            setCompletionStep('saving');
            setCompletionStepResults({ saving: { status: 'in-progress' } });
            setCompletionWarnings([]);
        }

        try {
            const response = await adminUpdateJob(jobId, updatePayload);
            console.log("adminUpdateJob response:", response);

            let saveMessage = null;
            if (isCompletionAction) {
                // Update stepper: saving done
                const newResults = { saving: { status: 'success' } };

                // Check if invoice/RCTI results were returned (nested under response.data)
                const jobResponseData = response.data || {};
                if (jobResponseData.invoiceRctiResults) {
                    // Invoice step
                    if (jobResponseData.invoiceRctiResults.invoice) {
                        newResults.invoice = jobResponseData.invoiceRctiResults.invoice;
                    } else {
                        newResults.invoice = { status: 'skipped' };
                    }
                    // RCTI step
                    if (jobResponseData.invoiceRctiResults.rcti) {
                        newResults.rcti = jobResponseData.invoiceRctiResults.rcti;
                    } else {
                        newResults.rcti = { status: 'skipped' };
                    }
                } else if (jobResponseData.invoiceRctiErrors && jobResponseData.invoiceRctiErrors.length > 0) {
                    // Had errors but no results — check what failed
                    const invoiceError = jobResponseData.invoiceRctiErrors.find(e => e.type === 'Invoice' || e.type === 'Monthly Allocation Check');
                    const rctiError = jobResponseData.invoiceRctiErrors.find(e => e.type === 'RCTI');
                    newResults.invoice = invoiceError
                        ? { status: 'failed', message: invoiceError.message }
                        : { status: 'skipped' };
                    newResults.rcti = rctiError
                        ? { status: 'failed', message: rctiError.message }
                        : { status: 'skipped' };
                } else {
                    // No invoice/RCTI results — likely monthly allocation in progress or carwash
                    newResults.invoice = { status: 'skipped' };
                    newResults.rcti = { status: 'skipped' };
                }

                setCompletionWarnings(jobResponseData.xeroSyncWarnings || []);
                setCompletionStepResults(newResults);
                setCompletionStep('done');
            } else {
                // Non-completion update — build message to show after re-fetch
                saveMessage = `Job ${jobId} updated successfully!`;
                if (response.status === 'completed' && response?.allocationType === 'monthly') {
                    saveMessage += ' Invoice and RCTI will be generated at the end of the month.';
                }
            }

            await fetchJob();
            fetchAuditLog();
            if (saveMessage) {
                setSaveSuccessMessage(saveMessage);
            }
        } catch (error) {
            if (isCompletionAction) {
                setCompletionStepResults(prev => ({
                    ...prev,
                    saving: { status: 'failed', message: error.message || 'Failed to update job' },
                    invoice: { status: 'skipped' },
                    rcti: { status: 'skipped' },
                }));
                setCompletionWarnings([]);
                setCompletionStep('done');
            } else {
                setApiError(error.message || 'Failed to update job. Please check details.');
            }
            console.error("Submission error:", error);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
            e.preventDefault();
        }
    };

    const handleCreateInvoiceAndRcti = async () => {
        setInvoiceRctiCreating(true);
        setInvoiceRctiResult(null);
        setApiError(null);
        
        try {
            const response = await createInvoiceAndRcti(jobId);
            setInvoiceRctiResult(response);
            
            // If successful or partial, refresh the job data
            if (response.status === 'success' || response.status === 'partial') {
                await fetchJob();
            }
        } catch (error) {
            setApiError(error.message || `Failed to ${invoiceRctiActionLabel.toLowerCase()}`);
            setInvoiceRctiResult({ status: 'fail', message: error.message });
        } finally {
            setInvoiceRctiCreating(false);
        }
    };

    const openInvoiceRctiModal = () => {
        setShowInvoiceRctiModal(true);
        setInvoiceRctiResult(null);
    };

    const closeInvoiceRctiModal = () => {
        setShowInvoiceRctiModal(false);
        setInvoiceRctiResult(null);
    };

    if (configLoading || loading) {
        return <p className="text-center text-gray-500 py-10">Loading job details...</p>;
    }
    if (configError) {
        return <p className="text-center text-red-500 bg-red-100 p-3 rounded-xl">Error loading configuration: {configError}</p>;
    }
    if (!originalJobData && !loading) {
         return (
            <div className="container mx-auto px-4 py-8 max-w-4xl text-center">
                 <p className="text-red-600 bg-red-100 p-3 rounded mb-4">{apiError || "Job not found."}</p>
                 <Link to={returnSearch ? { pathname: '/invoices', search: returnSearch } : "/jobs-list"} className="text-jims-cyan-dark hover:text-jims-cyan underline">
                    {returnSearch ? 'Back to Invoices' : 'Back to Jobs List'}
                </Link>
            </div>
         );
    }

    return (
        <div className="container mx-auto px-4 pt-8 pb-20 max-w-4xl">
            <div className="flex justify-between items-center mb-2">
                <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Edit Job</h1>
                <Link 
                    to={returnSearch ? { pathname: '/invoices', search: returnSearch } : "/jobs-list"}
                    state={returnSearch ? {} : { filters: filtersFromNav }}
                    className="text-jims-cyan-dark hover:text-jims-cyan underline"
                >
                    {returnSearch ? 'Back to Invoices' : 'Back to Jobs List'}
                </Link>
            </div>
            <div className="mb-6 text-sm text-gray-600">
                Job ID: <span className="font-medium text-gray-800">{jobId}</span>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className="bg-white p-8 rounded-3xl shadow-sm ring-1 ring-gray-900/5 space-y-6">
                <div ref={errorRef}>
                    {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded-xl mb-4">{apiError}</p>}
                    {saveSuccessMessage && <p className="text-center text-green-600 bg-green-100 p-3 rounded-xl mb-4">{saveSuccessMessage}</p>}
                    {statusChangeValidationError && (
                        <p className="text-center text-red-600 bg-red-100 p-3 rounded-xl mb-4 flex items-center justify-center">
                            <FaInfoCircle className="mr-2" /> {statusChangeValidationError}
                        </p>
                    )}
                </div>

                {isOverdue && (
                    <div className="mb-4 pb-3 border-b border-red-200">
                        <p className="text-xs text-red-600 flex items-center">
                            <FaExclamationTriangle className="inline mr-2 flex-shrink-0" />
                            Job is overdue. Contact the franchisee to reschedule or update status to Completed if job was already done.
                        </p>
                    </div>
                )}

                {isServiceDelayed && originalJobData?.reasonForDelay && (
                    <div className="p-3 bg-orange-100 text-orange-700 border border-orange-300 rounded-xl text-sm mb-4 flex items-center">
                        <FaInfoCircle className="inline mr-2 flex-shrink-0" />
                        Delay Reason: {originalJobData.reasonForDelay}
                    </div>
                )}

                {originalJobData?.notesForAdmin && (
                    <div className="p-3 bg-blue-100 text-blue-700 border border-blue-300 rounded-xl text-sm mb-4 flex items-center">
                        <FaInfoCircle className="inline mr-2 flex-shrink-0" />
                        Notes from franchisee: {originalJobData.notesForAdmin}
                    </div>
                )}

                {originalJobData?.isNoPhotoJob && (
                    <div className="p-3 bg-orange-100 text-orange-700 border border-orange-300 rounded-full text-xs italic mb-4">
                        Franchisee declared that there are no photos for this job.
                    </div>
                )}

                {(originalJobData?.status === 'completed' || originalJobData?.status === 'invoiced' || originalJobData?.invoiceId || originalJobData?.rctiId) && originalJobData?.completionDate && (
                    <div className="p-3 bg-green-50 text-green-700 border border-green-200 rounded text-sm mb-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <FaCalendarCheck className="inline mr-2 flex-shrink-0 text-green-600" />
                                Job Completed On: {formatTimestampForDisplay(originalJobData.completionDate, true)}
                            </div>
                            {!originalJobData.invoiceId || !originalJobData.rctiId ? (
                                <button
                                    type="button"
                                    onClick={openInvoiceRctiModal}
                                    className="ml-4 bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm transition duration-300 hover:bg-green-600-dark transition-colors duration-200 flex items-center text-sm font-medium"
                                >
                                    <FaFileInvoice className="mr-2" />
                                    {invoiceRctiActionLabel}
                                </button>
                            ) : (
                                <div className="ml-4 px-4 py-2 bg-gray-100 text-gray-500 rounded-full flex items-center text-sm font-medium">
                                    <FaCheck className="mr-2" />
                                    Invoice & RCTI Created
                                </div>
                            )}
                        </div>
                        {/* Invoice and RCTI Links with Void actions */}
                        <div className="mt-3 pt-3 border-t border-green-200">
                            {originalJobData.invoiceId || originalJobData.rctiId ? (
                                <div className="space-y-3">
                                    <div className="flex flex-wrap gap-4 items-center">
                                        {originalJobData.invoiceId && (
                                            <div className="inline-flex items-center gap-2">
                                                <Link
                                                    to={`/invoices/${originalJobData.invoiceId}`}
                                                    className={`font-medium inline-flex items-center ${isInvoiceVoided ? 'text-red-600 line-through' : 'text-jims-cyan-dark hover:text-jims-cyan'}`}
                                                >
                                                    <FaFileInvoice className="mr-2" />
                                                    View Invoice {originalJobData.invoiceNumber ? `(${originalJobData.invoiceNumber})` : ''}
                                                </Link>
                                                {isInvoiceVoided && (
                                                    <span className="text-xs text-red-600 font-semibold">(Voided)</span>
                                                )}
                                                {canVoidInvoice && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowVoidInvoiceConfirm(true)}
                                                        disabled={voidingInvoice}
                                                        className={`inline-flex items-center transition-colors ${
                                                            voidingInvoice ? 'text-gray-400 cursor-not-allowed' : 'text-red-500 hover:text-red-700'
                                                        }`}
                                                        title="Void Invoice"
                                                    >
                                                        {voidingInvoice ? (
                                                            <FaSync className="animate-spin" size={12} />
                                                        ) : (
                                                            <FaTrashAlt size={12} />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {originalJobData.rctiId && (
                                            <div className="inline-flex items-center gap-2">
                                                <Link
                                                    to={`/rctis/${originalJobData.rctiId}`}
                                                    className={`font-medium inline-flex items-center ${isRctiVoided ? 'text-red-600 line-through' : 'text-jims-cyan-dark hover:text-jims-cyan'}`}
                                                >
                                                    <FaFileInvoice className="mr-2" />
                                                    View RCTI {originalJobData.rctiNumber ? `(${originalJobData.rctiNumber})` : ''}
                                                </Link>
                                                {isRctiVoided && (
                                                    <span className="text-xs text-red-600 font-semibold">(Voided)</span>
                                                )}
                                                {canVoidRCTI && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowVoidRCTIConfirm(true)}
                                                        disabled={voidingRCTI}
                                                        className={`inline-flex items-center transition-colors ${
                                                            voidingRCTI ? 'text-gray-400 cursor-not-allowed' : 'text-red-500 hover:text-red-700'
                                                        }`}
                                                        title="Void RCTI"
                                                    >
                                                        {voidingRCTI ? (
                                                            <FaSync className="animate-spin" size={12} />
                                                        ) : (
                                                            <FaTrashAlt size={12} />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {invoiceGenerationFailureInfo && (
                                        <div className="rounded border border-red-200 bg-red-50 p-3">
                                            <p className="text-sm text-red-700 flex items-center">
                                                <FaExclamationTriangle className="mr-2 flex-shrink-0" />
                                                {invoiceGenerationFailureInfo.userFriendlyMessage}
                                            </p>
                                            {invoiceGenerationFailureInfo.technicalMessages.length > 0 && (
                                                <div className="mt-2 text-xs text-gray-600 italic space-y-1">
                                                    {invoiceGenerationFailureInfo.technicalMessages.map((message, index) => (
                                                        <p key={`${message}-${index}`}>{message}</p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                invoiceGenerationFailureInfo ? (
                                    <div className="rounded border border-red-200 bg-red-50 p-3">
                                        <p className="text-sm text-red-700 flex items-center">
                                            <FaExclamationTriangle className="mr-2 flex-shrink-0" />
                                            {invoiceGenerationFailureInfo.userFriendlyMessage}
                                        </p>
                                        {invoiceGenerationFailureInfo.technicalMessages.length > 0 && (
                                            <div className="mt-2 text-xs text-gray-600 italic space-y-1">
                                                {invoiceGenerationFailureInfo.technicalMessages.map((message, index) => (
                                                    <p key={`${message}-${index}`}>{message}</p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-xs text-orange-600 italic flex items-center">
                                        <FaInfoCircle className="mr-2" />
                                        There is no invoice and RCTI created for this job
                                    </p>
                                )
                            )}
                        </div>
                    </div>
                )}

                <div className="relative">
                    <label htmlFor="fseNameSearch" className="block text-sm font-medium text-gray-700">Franchisee *</label>
                    <input id="fseNameSearch" type="text" autoComplete="off"
                        {...register("fseNameSearch", { required: "Franchisee search is required." })}
                        value={watch('fseNameSearch')}
                        onChange={fseHandlers.onChange}
                        onBlur={() => setTimeout(() => franchiseeSearch.setShowSuggestions(false), 150)}
                        onFocus={() => franchiseeSearch.suggestions.length > 0 && franchiseeSearch.setShowSuggestions(true)}
                        className={`mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 ${errors.fseNameSearch || (errors.fseId && !watch('fseId')) ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    <input type="hidden" {...register("fseId", { required: "Please select a franchisee." })} />
                    {errors.fseId && !watch('fseId') && <p className="mt-1 text-xs text-red-500">{errors.fseId.message}</p>}
                    {franchiseeSearch.showSuggestions && (
                        <ul className="suggestions-list">
                            {franchiseeSearch.isSearching ? <li className="searching">Searching...</li> :
                             franchiseeSearch.suggestions.length > 0 ? franchiseeSearch.suggestions.map(item => (
                                <li key={item.id} className="suggestion-item" onMouseDown={() => fseHandlers.onSelect(item)}>
                                    {fseHandlers.renderSuggestions(item)}
                                </li>
                            )) : franchiseeSearch.searchTerm.length >= 3 && <li className="no-results">No franchisees found.</li>}
                        </ul>
                    )}
                </div>                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <ReadOnlyField label={terminology.client.singular} value={watch('clientName')} />
                    </div>
                    <div>
                        <ReadOnlyField label={terminology.household.singular} value={watch('householdName')} />
                        <p className="mt-1 text-xs text-gray-500">
                            <FaMapMarkerAlt className="inline mr-1"/> {watch('householdAddress')}
                        </p>
                    </div>
                </div>

                {/* NDIS Field Display */}
                {originalJobData?.ndisJob && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-500 mb-2">NDIS Service</label>
                        <div className="p-2 bg-green-50 border border-green-200 rounded-xl text-sm flex items-center">
                            <FaMedkit className="mr-2 text-green-600" />
                            <span className="text-green-700 font-medium">This is an NDIS service job</span>
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Services *</label>
                    {serviceTypesLoading ? (
                        <p className="text-gray-500">Loading services...</p>
                    ) : allServiceTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {allServiceTypes.map(service => {
                                const isSelected = selectedServiceIds.includes(service.id);
                                return (
                                    <button
                                        key={service.id}
                                        type="button"
                                        onClick={() => handleServiceToggle(service.id)}
                                        className={`
                                            px-3 py-1 rounded-full text-sm font-medium border transition-colors duration-150
                                            flex items-center justify-center
                                            ${isSelected
                                                ? 'bg-green-600 text-white border-green-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                            }
                                        `}
                                    >
                                        {isSelected && <FaCheck className="mr-1 text-xs" />}
                                        {service.name}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-gray-500">No services available.</p>
                    )}
                    {serviceSelectionError && <p className="mt-2 text-xs text-red-500">{serviceSelectionError}</p>}
                    <input type="hidden" {...register('servicesDirty')} />
                </div>

                {/* Job Frequency + Job Date + Job Time */}
                {originalJobData && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-2">Job Frequency</label>
                            <div className="p-3 bg-gray-100 border border-gray-300 rounded-xl text-sm text-gray-700 flex items-center">
                                <FaCalendarAlt className="mr-2 text-green-600" />
                                {originalJobData.scheduleType === 'one-off' ? (
                                    <span>One-off</span>
                                ) : originalJobData.scheduleType === 'recurring' ? (
                                    <span>
                                        Recurring: {formatters.formatFrequency(originalJobData.frequencyType, originalJobData.frequencyValue)}
                                    </span>
                                ) : (
                                    <span>{originalJobData.scheduleType || 'N/A'}</span>
                                )}
                            </div>
                        </div>
                        <div>
                            <InputField
                                label="Job Date"
                                id="jobDate"
                                type="date"
                                register={register("jobDate")}
                                error={errors.jobDate}
                                disabled={isDateTimeLocked}
                                className={isDateTimeLocked ? 'bg-gray-100 cursor-not-allowed' : ''}
                            />
                        </div>
                        <div>
                            <InputField
                                label="Job Time"
                                id="jobTime"
                                type="time"
                                register={register("jobTime")}
                                error={errors.jobTime}
                                disabled={isDateTimeLocked}
                                className={isDateTimeLocked ? 'bg-gray-100 cursor-not-allowed' : ''}
                            />
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    {/* Allocation Type Pills */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Allocation Type *
                        </label>
                        <div className="flex space-x-4">
                            {allocationTypes.map(type => (
                                <label key={type.value} className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        value={type.value}
                                        {...register("allocationType", { required: "Please select an allocation type." })}
                                        className="sr-only"
                                    />
                                    <div className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all ${
                                        allocationType === type.value 
                                            ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}>
                                        {type.label}
                                    </div>
                                </label>
                            ))}
                        </div>
                        {errors.allocationType && <p className="mt-1 text-xs text-red-500">{errors.allocationType.message}</p>}
                    </div>
                    <div>
                        <InputField
                            label={allocationType === 'fixed-fee' ? 'Fixed allocated hours' : 'Allotted Hours'}
                            id="allotedHours" type="number" step="0.1" min="0"
                            register={register("allotedHours", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                            error={errors.allotedHours}
                            placeholder="e.g., 1.5"
                        />
                    </div>
                    <div>
                        <InputField
                            label={`Actual Hours ${currentStatus === 'completed' && allocationType !== 'fixed-fee' ? '*' : ''}`}
                            id="actualHours" type="number" step="0.1" min="0"
                            register={register("actualHours", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                            error={errors.actualHours}
                            placeholder="e.g., 1.0"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                    <div>
                        <InputField label="FSE Quote /hour" id="priceQuotePerHour" type="number" step="0.01"
                            register={register("priceQuotePerHour")}
                            readOnly disabled
                            className="bg-gray-100 cursor-not-allowed"
                            placeholder="N/A"
                        />
                    </div>
                    <div>
                        <InputField label="FSE price/hour (w/ GST)" id="pricePerHour" type="number" step="0.01" min="0"
                            register={register("pricePerHour", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                            error={errors.pricePerHour}
                            placeholder="e.g., 55.00"
                        />
                    </div>
                    <div>
                        <InputField label="Invoice Price (ex. GST)" id="additionalPrice" type="number" step="0.01" min="0"
                            register={register("additionalPrice", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                            error={errors.additionalPrice}
                            placeholder="e.g., 77.00"
                        />
                    </div>
                    <div>
                        <InputField label="Invoice price/hour (w/ GST)" id="invoicePricePerHour" type="number" step="0.01" min="0"
                            register={register("invoicePricePerHour", { valueAsNumber: true, min: { value: 0, message: "Cannot be negative." } })}
                            error={errors.invoicePricePerHour}
                            placeholder="e.g., 60.50"
                        />
                    </div>
                </div>

                <div>
                    <TextareaField label="Description (Invoice Notes)" id="description" rows={3}
                        register={register("description")}
                        error={errors.description}
                        placeholder="Enter notes that will appear on the client invoice..."
                    />
                </div>

                <div>
                    <TextareaField label="Admin Notes" id="adminNotes" rows={3}
                        register={register("adminNotes")}
                        error={errors.adminNotes}
                        placeholder="Internal notes for admin use only..."
                    />
                </div>

                <div>
                    <TextareaField label="Notes for franchisee" id="notesForFranchisee" rows={3}
                        register={register("notesForFranchisee")}
                        error={errors.notesForFranchisee}
                        placeholder="Enter any specific instructions or notes for the franchisee..."
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div>
                        <SelectField label="Status *" id="status"
                            register={register("status", { required: "Status is required." })}
                            error={errors.status}
                            disabled={jobStatuses.length === 0}
                        >
                            <option value="">Select Status...</option>
                            {jobStatuses.map(statusOption => (
                                <option key={statusOption.code} value={statusOption.code}>
                                    {statusOption.display}
                                </option>
                            ))}
                        </SelectField>
                        {jobStatuses.length === 0 && !configLoading && <p className="mt-1 text-xs text-red-500">Could not load status options.</p>}
                    </div>
                    <div>
                        <label htmlFor="completionDate" className="block text-sm font-medium text-gray-700">
                            Completion Date {currentStatus === 'completed' ? '*' : ''}
                        </label>
                        <input id="completionDate" type="date"
                            max={new Date().toISOString().split('T')[0]}
                            {...register("completionDate", {
                                required: currentStatus === 'completed' ? "Completion date is required when status is completed." : false,
                                validate: value => {
                                    if (value && value > new Date().toISOString().split('T')[0]) {
                                        return 'Completion date cannot be a future date.';
                                    }
                                    return true;
                                }
                            })}
                            className={`mt-1 block w-full input-field ${errors.completionDate ? 'border-red-500' : 'border-gray-300'}`}
                        />
                    </div>
                    <div>
                        <ReadOnlyField label="Xero Invoice Status" value={originalJobData?.xero_invoice_status || 'N/A'} />
                    </div>
                </div>

               <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
                      <div className="container mx-auto px-6 py-3 max-w-4xl flex justify-end gap-3">
                    <Link
                        to="/jobs-list"
                        state={{ filters: filtersFromNav }}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </Link>
                    <Button type="submit" variant="primary" disabled={isSubmitting || franchiseeSearch.isSearching || (!isDirty && !watch('servicesDirty'))}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                    </div>
                </div>

                <style jsx>{`
                    .block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 { padding: 0.5rem 0.75rem; border-width: 1px; border-radius: 1rem; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); outline: none; transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out; }
                    .block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6:focus { border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.3); }
                    .suggestions-list { position: absolute; z-index: 10; width: 100%; background-color: white; border: 1px solid #D1D5DB; border-radius: 1rem; margin-top: 0.25rem; max-height: 15rem; overflow-y: auto; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
                    .suggestion-item { padding: 0.5rem 0.75rem; cursor: pointer; }
                    .suggestion-item:hover { background-color: #67E8F9; }
                    .searching, .no-results { padding: 0.5rem 0.75rem; color: #6B7280; }
                    .btn { padding: 0.5rem 1rem; font-weight: 700; border-radius: 0.375rem; transition: background-color 0.3s ease, opacity 0.3s ease; border: 1px solid transparent; cursor: pointer; }
                    .btn:disabled { opacity: 0.5; cursor: not-allowed; }
                    .btn-primary { background-color: #06B6D4; color: white; }
                    .btn-primary:hover:not(:disabled) { background-color: #0E7490; }
                    .btn-secondary { background-color: #E5E7EB; color: #1F2937; }
                    .btn-secondary:hover:not(:disabled) { background-color: #D1D5DB; }
                `}</style>
            </form>

            {/* Activity Log Section */}
            {originalJobData && (
                <div className="mt-6 bg-white rounded-lg shadow-md overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setAuditLogExpanded(!auditLogExpanded)}
                        className="w-full flex items-center justify-between px-8 py-5 text-left hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center">
                            <FaHistory className="mr-3 text-jims-cyan-dark text-base" />
                            <span className="text-base font-semibold text-gray-700">Activity Log</span>
                            {auditLog.length > 0 && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 font-medium">
                                    {auditLog.length} update{auditLog.length !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                        {auditLogExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                    </button>

                    {auditLogExpanded && (
                        <div className="px-8 pb-6 border-t border-gray-100">
                            {auditLogLoading ? (
                                <p className="text-gray-500 text-sm py-4 text-center">Loading activity...</p>
                            ) : (
                                <div className="mt-4">
                                    {auditLog.map((entry) => {
                                        const ts = entry.timestamp?._seconds
                                            ? new Date(entry.timestamp._seconds * 1000)
                                            : null;
                                        const timeStr = ts
                                            ? ts.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', hour12: true })
                                            : 'Unknown time';
                                        const sourceBadgeClass = {
                                            admin: 'bg-blue-100 text-blue-700',
                                            franchisee: 'bg-green-100 text-green-700',
                                            system: 'bg-gray-100 text-gray-600',
                                        }[entry.source] || 'bg-gray-100 text-gray-600';
                                        return (
                                            <div key={entry.id} className="flex gap-3 py-3 border-b border-gray-100">
                                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-jims-cyan flex items-center justify-center mt-0.5">
                                                    <FaHistory className="text-white" style={{ fontSize: '10px' }} />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                                        <span className="text-sm font-medium text-gray-800">{entry.performedBy?.email || 'Unknown'}</span>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sourceBadgeClass}`}>{entry.source}</span>
                                                        <span className="text-xs text-gray-400">{timeStr}</span>
                                                    </div>
                                                    {entry.changes && entry.changes.length > 0 && (
                                                        <ul className="text-xs text-gray-600 space-y-0.5 mt-1">
                                                            {entry.changes.map((change, ci) => (
                                                                <li key={ci} className="flex flex-wrap gap-1 items-center">
                                                                    <span className="font-medium text-gray-700 capitalize">
                                                                        {change.field.replace(/([A-Z])/g, ' $1').trim()}:
                                                                    </span>
                                                                    {change.from !== null && change.from !== undefined && (
                                                                        <>
                                                                            <span className="line-through text-red-400">{change.from}</span>
                                                                            <span className="text-gray-400">→</span>
                                                                        </>
                                                                    )}
                                                                    <span className="text-green-700 font-medium">
                                                                        {change.to !== null && change.to !== undefined
                                                                            ? change.to
                                                                            : <em className="text-gray-400 font-normal">cleared</em>}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Synthetic Job Created entry */}
                                    {originalJobData.createdAt && (
                                        <div className="flex gap-3 py-3">
                                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mt-0.5">
                                                <FaCheck className="text-white" style={{ fontSize: '10px' }} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                    <span className="text-sm font-medium text-gray-800">Job created</span>
                                                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">system</span>
                                                    {originalJobData.createdAt._seconds && (
                                                        <span className="text-xs text-gray-400">
                                                            {new Date(originalJobData.createdAt._seconds * 1000).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', hour12: true })}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500">Job record created in the system.</p>
                                            </div>
                                        </div>
                                    )}

                                    {auditLog.length === 0 && !originalJobData.createdAt && (
                                        <p className="text-gray-400 text-sm py-4 text-center">No activity recorded yet.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Completion Progress Modal */}
            <CompletionProgressModal
                isOpen={showCompletionProgress}
                currentStep={completionStep}
                stepResults={completionStepResults}
                warnings={completionWarnings}
                onClose={() => {
                    setShowCompletionProgress(false);
                    setCompletionStep('saving');
                    setCompletionStepResults({});
                    setCompletionWarnings([]);
                }}
            />

            {/* Invoice & RCTI Creation Modal */}
            {showInvoiceRctiModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl shadow-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <FaFileInvoice className="mr-2 text-jims-cyan" />
                            {invoiceRctiActionLabel}
                        </h3>
                        
                        {!invoiceRctiResult ? (
                            <>
                                <p className="text-gray-600 mb-6">
                                    This will manually {invoiceRctiActionLabel.toLowerCase()} for this completed job,
                                    bypassing automatic creation rules.
                                    {originalJobData?.allocationType === 'monthly' && (
                                        <span className="block mt-2 text-sm text-orange-600">
                                            <FaInfoCircle className="inline mr-1" />
                                            Note: This is a monthly allocation job. Invoice/RCTI are normally created at month-end.
                                        </span>
                                    )}
                                </p>
                                <p className="text-sm text-gray-500 mb-6">
                                    Job ID: <span className="font-mono font-medium">{jobId}</span>
                                </p>
                                
                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={closeInvoiceRctiModal}
                                        disabled={invoiceRctiCreating}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCreateInvoiceAndRcti}
                                        disabled={invoiceRctiCreating}
                                        className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm transition duration-300 hover:bg-green-600-dark transition-colors disabled:opacity-50 flex items-center"
                                    >
                                        {invoiceRctiCreating ? (
                                            <>Creating...</>
                                        ) : (
                                            <>
                                                <FaFileInvoice className="mr-2" />
                                                {invoiceRctiActionLabel}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {invoiceRctiResult.status === 'success' && (
                                    <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded-xl flex items-start">
                                        <FaCheck className="mr-2 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-medium mb-2">Successfully created!</p>
                                            {invoiceRctiResult.invoiceId && (
                                                <p className="text-sm">Invoice ID: {invoiceRctiResult.invoiceId}</p>
                                            )}
                                            {invoiceRctiResult.rctiId && (
                                                <p className="text-sm">RCTI ID: {invoiceRctiResult.rctiId}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                {invoiceRctiResult.status === 'partial' && (
                                    <div className="mb-4 p-3 bg-yellow-100 text-yellow-700 border border-yellow-300 rounded-xl">
                                        <p className="font-medium mb-2 flex items-center">
                                            <FaExclamationTriangle className="mr-2" />
                                            Partially successful
                                        </p>
                                        <div className="text-sm space-y-1">
                                            {invoiceRctiResult.invoiceId && (
                                                <p className="text-green-700">✓ Invoice created: {invoiceRctiResult.invoiceId}</p>
                                            )}
                                            {invoiceRctiResult.invoiceError && (
                                                <p className="text-red-700">✗ Invoice failed: {invoiceRctiResult.invoiceError}</p>
                                            )}
                                            {invoiceRctiResult.rctiId && (
                                                <p className="text-green-700">✓ RCTI created: {invoiceRctiResult.rctiId}</p>
                                            )}
                                            {invoiceRctiResult.rctiError && (
                                                <p className="text-red-700">✗ RCTI failed: {invoiceRctiResult.rctiError}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                {invoiceRctiResult.status === 'fail' && (
                                    <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded-xl flex items-start">
                                        <FaExclamationTriangle className="mr-2 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-medium mb-2">Failed to create</p>
                                            <p className="text-sm">{invoiceRctiResult.message || 'Unknown error occurred'}</p>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={closeInvoiceRctiModal}
                                        className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm transition duration-300 hover:bg-green-600-dark transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Void Invoice Confirmation Modal */}
            <ConfirmationModal
                isOpen={showVoidInvoiceConfirm}
                onClose={() => setShowVoidInvoiceConfirm(false)}
                onConfirm={handleVoidInvoice}
                title="Void Invoice"
                message={`Are you sure you want to void invoice ${originalJobData?.invoiceNumber || ''}? This action cannot be undone. If the invoice has been sent to Xero, it will also be voided there.`}
                confirmText="Void Invoice"
                variant="danger"
                isLoading={voidingInvoice}
                loadingText="Voiding..."
            />

            {/* Void RCTI Confirmation Modal */}
            <ConfirmationModal
                isOpen={showVoidRCTIConfirm}
                onClose={() => setShowVoidRCTIConfirm(false)}
                onConfirm={handleVoidRCTI}
                title="Void RCTI"
                message={`Are you sure you want to void RCTI ${originalJobData?.rctiNumber || ''}? This action cannot be undone. If the RCTI has been sent to Xero, it will also be voided there.`}
                confirmText="Void RCTI"
                variant="danger"
                isLoading={voidingRCTI}
                loadingText="Voiding..."
            />
        </div>
    );
};

export default EditJobPage;
